import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { NodeResizer } from 'reactflow';
import { Typography, Input } from 'antd';
import { 
  MessageOutlined, 
  FontSizeOutlined,
  BorderOutlined
} from '@ant-design/icons';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

/**
 * DecorationNode - Purely visual nodes for workflow documentation
 * These nodes do NOT affect workflow execution logic
 */
const DecorationNode = React.memo(({ id, data, selected }) => {
  const nodeType = data.nodeType || data.type;
  const config = data.config || {};
  
  // State for inline editing - removed inline editing to avoid lag
  // Editing is done via PropertyDrawer instead

  const getDefaultText = useCallback((type) => {
    switch (type) {
      case 'comment':
        return 'Double-click to add comment...';
      case 'text_annotation':
        return 'Text';
      case 'shape_rectangle':
        return '';
      default:
        return '';
    }
  }, []);

  // Memoize styles to prevent re-renders
  const commentStyles = useMemo(() => {
    const backgroundColor = config.backgroundColor || '#fffbe6';
    const borderColor = config.borderColor || '#ffe58f';
    const textColor = config.textColor || '#8c6800';
    
    return {
      container: {
        minWidth: config.width || 200,
        minHeight: config.height || 80,
        backgroundColor,
        border: `2px solid ${borderColor}`,
        borderRadius: '12px',
        padding: '12px 16px',
        boxShadow: selected ? `0 0 0 2px ${borderColor}` : '0 2px 8px rgba(0,0,0,0.1)',
        position: 'relative',
        cursor: 'default'
      },
      tailOuter: {
        position: 'absolute',
        bottom: '-10px',
        left: '20px',
        width: 0,
        height: 0,
        borderLeft: '10px solid transparent',
        borderRight: '10px solid transparent',
        borderTop: `10px solid ${borderColor}`,
      },
      tailInner: {
        position: 'absolute',
        bottom: '-6px',
        left: '22px',
        width: 0,
        height: 0,
        borderLeft: '8px solid transparent',
        borderRight: '8px solid transparent',
        borderTop: `8px solid ${backgroundColor}`,
      },
      header: { 
        display: 'flex', 
        alignItems: 'center', 
        gap: 8, 
        marginBottom: 8,
        color: textColor 
      },
      content: {
        margin: 0,
        color: textColor,
        fontSize: 13,
        lineHeight: 1.5,
        whiteSpace: 'pre-wrap',
        cursor: 'pointer'
      },
      textColor,
      title: config.title || 'Comment' // Use title from config, fallback to 'Comment'
    };
  }, [config.backgroundColor, config.borderColor, config.textColor, config.width, config.height, config.title, selected]);

  // Render Comment Bubble
  const renderComment = () => {
    return (
      <div style={commentStyles.container}>
        {/* Comment tail */}
        <div style={commentStyles.tailOuter} />
        <div style={commentStyles.tailInner} />

        {/* Header with icon and title */}
        <div style={commentStyles.header}>
          <MessageOutlined style={{ fontSize: 14 }} />
          <Text strong style={{ fontSize: 12, color: commentStyles.textColor }}>
            {commentStyles.title}
          </Text>
        </div>

        {/* Content - click to select node and edit in PropertyDrawer */}
        <Paragraph style={commentStyles.content}>
          {config.text || getDefaultText(nodeType)}
        </Paragraph>
      </div>
    );
  };

  // Memoize text annotation styles
  const textStyles = useMemo(() => {
    const textColor = config.textColor || '#262626';
    const fontSize = config.fontSize || 16;
    const fontWeight = config.fontWeight || 'normal';
    const fontStyle = config.fontStyle || 'normal';
    const textAlign = config.textAlign || 'left';

    return {
      container: {
        minWidth: 100,
        padding: '8px 12px',
        cursor: 'default'
      },
      text: {
        color: textColor,
        fontSize,
        fontWeight,
        fontStyle,
        textAlign,
        display: 'block',
        cursor: 'pointer',
        borderBottom: selected ? '2px dashed #4096ff' : 'none',
        paddingBottom: 2
      }
    };
  }, [config.textColor, config.fontSize, config.fontWeight, config.fontStyle, config.textAlign, selected]);

  // Render Text Annotation
  const renderTextAnnotation = () => {
    return (
      <div style={textStyles.container}>
        <Text style={textStyles.text}>
          {config.text || getDefaultText(nodeType)}
        </Text>
      </div>
    );
  };

  // Memoize rectangle styles
  const rectangleStyles = useMemo(() => {
    const backgroundColor = config.backgroundColor || 'rgba(24, 144, 255, 0.05)';
    const borderColor = config.borderColor || '#1890ff';
    const borderStyle = config.borderStyle || 'dashed';
    const borderWidth = config.borderWidth || 2;
    const borderRadius = config.borderRadius || 8;
    const width = config.width || 200;
    const height = config.height || 120;

    return {
      container: {
        width: width,
        height: height,
        backgroundColor,
        border: `${borderWidth}px ${borderStyle} ${borderColor}`,
        borderRadius,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        padding: 12,
        boxSizing: 'border-box',
        position: 'relative',
        pointerEvents: 'none' // Center area doesn't block clicks
      },
      label: {
        fontSize: 12,
        fontWeight: 600,
        color: borderColor,
        backgroundColor: 'rgba(255,255,255,0.8)',
        padding: '2px 6px',
        borderRadius: 4,
        cursor: 'pointer'
      },
      borderColor,
      width,
      height
    };
  }, [config.backgroundColor, config.borderColor, config.borderStyle, config.borderWidth, config.borderRadius, config.width, config.height]);

  // Render Rectangle Shape
  const renderRectangle = () => {
    return (
      <>
        <NodeResizer
          color={rectangleStyles.borderColor}
          isVisible={selected}
          minWidth={100}
          minHeight={60}
          handleStyle={{
            width: 8,
            height: 8,
            borderRadius: 2
          }}
        />
        <div style={rectangleStyles.container}>
          {/* Clickable border area - thin transparent overlay on border */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            border: '8px solid transparent', // Clickable area on border
            pointerEvents: 'auto',
            cursor: 'move'
          }} />
          
          {/* Optional label */}
          {config.text && (
            <div style={{ width: '100%', position: 'relative', zIndex: 1, pointerEvents: 'auto' }}>
              <Text style={rectangleStyles.label}>
                {config.text}
              </Text>
            </div>
          )}
        </div>
      </>
    );
  };

  // Render based on node type
  switch (nodeType) {
    case 'comment':
      return renderComment();
    case 'text_annotation':
      return renderTextAnnotation();
    case 'shape_rectangle':
      return renderRectangle();
    default:
      return (
        <div style={{ padding: 10, backgroundColor: '#f0f0f0', borderRadius: 4 }}>
          <Text type="secondary">Unknown decoration</Text>
        </div>
      );
  }
}, (prevProps, nextProps) => {
  // Shallow compare for better performance
  if (prevProps.id !== nextProps.id || prevProps.selected !== nextProps.selected) {
    return false;
  }
  
  // Deep compare config only
  const prevConfig = prevProps.data?.config || {};
  const nextConfig = nextProps.data?.config || {};
  
  return JSON.stringify(prevConfig) === JSON.stringify(nextConfig);
});

export default DecorationNode;
